/**
 * useApiStore — reemplaza useStore() con datos reales de la API.
 * Mantiene la misma interfaz { store, dispatch } para compatibilidad con los módulos.
 * Lee de PostgreSQL (via API) y usa localStorage como caché local.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { getStore, updateStore } from '../store';
import {
  getToken, apiMe, consumeBootstrapPendiente,
  apiGetSolicitudes, apiGetSolicitudesDisponibles,
  apiGetViajesActivos, apiGetViajesEmpresa,
  apiGetMisOrdenes, apiGetHistorialChofer,
  apiGetNotificaciones,
  apiGetRetornosDisponibles, apiGetRetornosEmpresa,
  apiGetPlantilla, apiGetFlota, apiGetMantenimiento,
  apiGetTodasEmpresas, apiGetEmpresasPendientes, apiGetUsuarios, apiGetKpis,
  apiAdminGetSolicitudes, apiAdminGetViajes, apiAdminGetRetornos,
  apiGetReporteViajes, apiGetComisiones,
  apiGetConfig, apiGetMiEmpresa,
  apiGetMisReferidos, apiGetMisRetiros, apiGetRetiros,
  apiMarcarLeidas, apiGetTicketsSoporte, apiGetSoporteProMensajes,
} from '../api';

// Cache de config plataforma — raramente cambia, evitamos pedirla en cada poll
let _configCache = { value: null, ts: 0 };
const CONFIG_TTL_MS = 5 * 60 * 1000; // 5 minutos
const getCachedConfig = async () => {
  const now = Date.now();
  if (_configCache.value && now - _configCache.ts < CONFIG_TTL_MS) return _configCache.value;
  const value = await apiGetConfig();
  _configCache = { value, ts: now };
  return value;
};
export const invalidarCacheConfig = () => { _configCache = { value: null, ts: 0 }; };

// Cuáles keys del store se sobrescriben con datos reales de la API según el rol
const fetchersPorRol = {
  // Poll ligero para cliente — solo lo que cambia frecuentemente
  cliente_poll: async () => {
    const results = await Promise.allSettled([
      apiGetSolicitudes(),
      apiGetViajesActivos(),
      apiGetNotificaciones(),
      apiGetRetornosDisponibles(),
    ]);
    const [solicitudesRes, viajes, notificaciones, retornos] = results;
    const solicitudesData = solicitudesRes.status === 'fulfilled' ? solicitudesRes.value : null;
    const cotizaciones = solicitudesData
      ? solicitudesData.flatMap(s => (s.cotizaciones || []).map(c => ({ ...c, solicitudId: c.solicitudId || s.id })))
      : null;
    return {
      ...(solicitudesData ? { solicitudes: solicitudesData } : {}),
      ...(cotizaciones ? { cotizaciones } : {}),
      ...(viajes.status === 'fulfilled' ? { viajes: viajes.value } : {}),
      ...(notificaciones.status === 'fulfilled' ? { notificaciones: notificaciones.value } : {}),
      ...(retornos.status === 'fulfilled' ? { retornosVacios: retornos.value } : {}),
    };
  },

  cliente: async () => {
    const results_cliente = await Promise.allSettled([
      apiGetSolicitudes(),
      apiGetViajesActivos(),
      apiGetNotificaciones(),
      apiGetRetornosDisponibles(),
      apiGetMisReferidos(),
      apiMe(),
      apiGetMisRetiros(),
      getCachedConfig(),
    ]);
    results_cliente.filter(r => r.status === 'rejected').forEach(r => console.warn('[useApiStore] Fetch parcial fallido:', r.reason?.message));
    const [solicitudesRes, viajes, notificaciones, retornos, misReferidos, perfilRes, misRetiros, config] = results_cliente;
    // Extraer cotizaciones anidadas de las solicitudes
    const solicitudesData = solicitudesRes.status === 'fulfilled' ? solicitudesRes.value : null;
    const cotizaciones = solicitudesData
      ? solicitudesData.flatMap(s => (s.cotizaciones || []).map(c => ({ ...c, solicitudId: c.solicitudId || s.id })))
      : null;
    const referidosData = misReferidos.status === 'fulfilled' ? misReferidos.value : null;
    return {
      ...(solicitudesData ? { solicitudes: solicitudesData } : {}),
      ...(cotizaciones ? { cotizaciones } : {}),
      ...(viajes.status === 'fulfilled' ? { viajes: viajes.value } : {}),
      ...(notificaciones.status === 'fulfilled' ? { notificaciones: notificaciones.value } : {}),
      ...(retornos.status === 'fulfilled' ? { retornosVacios: retornos.value } : {}),
      ...(perfilRes.status === 'fulfilled' && perfilRes.value ? { usuarioPerfil: perfilRes.value } : {}),
      ...(referidosData ? {
        misReferidos: referidosData.referidos,
        misReferidosN1: referidosData.n1 || referidosData.referidos,
        misReferidosN2: referidosData.n2 || [],
        misReferidosN3: referidosData.n3 || [],
        redResumen: referidosData.resumen || null,
        comisionesRed: referidosData.comisiones,
      } : {}),
      ...(misRetiros.status === 'fulfilled' ? { retirosFondos: misRetiros.value } : {}),
      ...(config.status === 'fulfilled' && config.value ? { configPlataforma: config.value } : {}),
    };
  },

  // Carga completa del chofer (solo al iniciar sesión)
  chofer: async () => {
    const results_chofer = await Promise.allSettled([
      apiGetMisOrdenes(),
      apiGetHistorialChofer(),
      apiGetNotificaciones(),
      apiMe(),
    ]);
    results_chofer.filter(r => r.status === 'rejected').forEach(r => console.warn('[useApiStore] Fetch parcial fallido:', r.reason?.message));
    const [ordenes, historial, notificaciones, perfilRes] = results_chofer;
    return {
      ...(ordenes.status === 'fulfilled' ? { ordenesChofer: ordenes.value } : {}),
      ...(historial.status === 'fulfilled' ? { historialEntregas: historial.value } : {}),
      ...(notificaciones.status === 'fulfilled' ? { notificaciones: notificaciones.value } : {}),
      ...(perfilRes.status === 'fulfilled' && perfilRes.value ? { usuarioPerfil: perfilRes.value } : {}),
    };
  },
  // Poll ligero del chofer (solo órdenes + notificaciones, sin historial ni perfil)
  chofer_poll: async () => {
    const results = await Promise.allSettled([
      apiGetMisOrdenes(),
      apiGetNotificaciones(),
    ]);
    const [ordenes, notificaciones] = results;
    return {
      ...(ordenes.status === 'fulfilled' ? { ordenesChofer: ordenes.value } : {}),
      ...(notificaciones.status === 'fulfilled' ? { notificaciones: notificaciones.value } : {}),
    };
  },

  transportista: async () => {
    const results_trans = await Promise.allSettled([
      apiGetMiEmpresa(),
      apiGetViajesEmpresa(),
      apiGetSolicitudesDisponibles(),
      apiGetRetornosEmpresa(),
      apiGetPlantilla(),
      apiGetFlota(),
      apiGetNotificaciones(),
      getCachedConfig(),
      apiGetMantenimiento(),
    ]);
    results_trans.filter(r => r.status === 'rejected').forEach(r => console.warn('[useApiStore] Fetch parcial fallido:', r.reason?.message));
    const [miEmpresa, viajes, solicitudes, retornos, plantilla, flota, notificaciones, config, mantenimientos] = results_trans;
    // Si la empresa está pendiente solo guardamos miEmpresa, el resto falla igual pero no importa
    return {
      ...(miEmpresa.status === 'fulfilled' && miEmpresa.value ? { miEmpresa: miEmpresa.value } : {}),
      ...(viajes.status === 'fulfilled' ? { viajes: viajes.value } : {}),
      ...(solicitudes.status === 'fulfilled' ? {
        solicitudes: solicitudes.value,
        cotizaciones: solicitudes.value.flatMap(s => (s.cotizaciones || []).map(c => ({ ...c, solicitudId: c.solicitudId || s.id })))
      } : {}),
      ...(retornos.status === 'fulfilled' ? { retornosVacios: retornos.value } : {}),
      ...(plantilla.status === 'fulfilled' ? { plantillaChoferes: plantilla.value } : {}),
      ...(flota.status === 'fulfilled' ? { flota: flota.value } : {}),
      ...(notificaciones.status === 'fulfilled' ? { notificaciones: notificaciones.value } : {}),
      ...(config.status === 'fulfilled' && config.value ? { configPlataforma: config.value } : {}),
      ...(mantenimientos.status === 'fulfilled' ? { mantenimientos: mantenimientos.value } : {}),
    };
  },

  superadmin: async () => {
    const results_admin = await Promise.allSettled([
      apiGetTodasEmpresas(),
      apiGetEmpresasPendientes(),
      apiGetUsuarios(),
      apiGetKpis(),
      apiGetReporteViajes(),
      apiGetComisiones(),
      getCachedConfig(),
      apiGetNotificaciones(),
      apiGetRetiros(),
      apiAdminGetSolicitudes(),
      apiAdminGetViajes(),
      apiAdminGetRetornos(),
      apiGetTicketsSoporte(),
      apiGetSoporteProMensajes(),
    ]);
    results_admin.filter(r => r.status === 'rejected').forEach(r => console.warn('[useApiStore] Fetch parcial fallido:', r.reason?.message));
    const [empresas, pendientes, usuarios, kpis, reporteViajes, comisiones, config, notificaciones, retiros, solicitudes, viajes, retornos, tickets, soportePro] = results_admin;
    const empresasData = empresas.status === 'fulfilled' ? empresas.value : null;
    const plantillaChoferes = empresasData
      ? empresasData.flatMap(e => (e.plantillaChoferes || []).map(c => ({ ...c, empresaId: e.id, empresaNombre: e.nombre })))
      : null;

    return {
      ...(empresasData ? { empresasActivas: empresasData, plantillaChoferes } : {}),
      ...(pendientes.status === 'fulfilled' ? { empresasPendientes: pendientes.value } : {}),
      ...(usuarios.status === 'fulfilled' ? { usuarios: usuarios.value } : {}),
      ...(kpis.status === 'fulfilled' ? { _kpis: kpis.value } : {}),
      ...(reporteViajes.status === 'fulfilled' ? { reporteViajes: reporteViajes.value } : {}),
      ...(comisiones.status === 'fulfilled' ? { comisionesRed: comisiones.value } : {}),
      ...(config.status === 'fulfilled' && config.value ? { configPlataforma: config.value } : {}),
      ...(notificaciones.status === 'fulfilled' ? { notificaciones: notificaciones.value } : {}),
      ...(retiros.status === 'fulfilled' ? { retirosFondos: retiros.value } : {}),
      ...(solicitudes.status === 'fulfilled' ? { solicitudes: solicitudes.value.map(s => ({ ...s, clienteNombre: s.clienteNombre || s.cliente?.nombre || '' })) } : {}),
      ...(viajes.status === 'fulfilled' ? { viajes: viajes.value.map(v => ({ ...v, clienteNombre: v.clienteNombre || v.cliente?.nombre || '', transportistaNombre: v.transportistaNombre || v.empresa?.nombre || '' })) } : {}),
      ...(retornos.status === 'fulfilled' ? { retornosVacios: retornos.value.map(r => ({ ...r, empresaNombre: r.empresaNombre || r.empresa?.nombre || '' })) } : {}),
      ...(tickets.status === 'fulfilled' || soportePro.status === 'fulfilled' ? {
        mensajes: [
          ...(tickets.status === 'fulfilled' ? tickets.value : []),
          ...(soportePro.status === 'fulfilled' ? soportePro.value : []),
        ]
      } : {}),
    };
  },
};

// admin/operador/soporte usan mismo fetcher que superadmin
fetchersPorRol.admin = fetchersPorRol.superadmin;
fetchersPorRol.operador = fetchersPorRol.superadmin;
fetchersPorRol.soporte = fetchersPorRol.superadmin;

const NOTIF_VENTANA_MS = 48 * 60 * 60 * 1000;

export const useApiStore = (rol) => {
  const [store, setStore] = useState(getStore);
  const [cargandoApi, setCargandoApi] = useState(false);
  const intervalRef = useRef(null);
  const pausadoRef = useRef(false);
  const fallosConsecutivosRef = useRef(0);
  const intervaloActualRef = useRef(rol === 'chofer' ? 20000 : 15000);
  const pollCountRef = useRef(0);
  // IDs de notificaciones ya vistas — persiste en localStorage para sobrevivir recargas
  const STORAGE_KEY = `choyero_notif_vistas_${rol}`;
  const getVistas = () => {
    try { return new Set(JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]')); } catch { return new Set(); }
  };
  const addVistas = (ids) => {
    try {
      const prev = getVistas();
      ids.forEach(id => prev.add(id));
      // Limitar a los últimos 500 IDs para no crecer indefinidamente
      const arr = [...prev];
      localStorage.setItem(STORAGE_KEY, JSON.stringify(arr.slice(-500)));
    } catch { /* ignorar errores de storage */ }
  };
  const iniciado = useRef(false);

  // Cargar datos reales de la API
  const recargar = useCallback(async () => {
    if (!getToken() || !rol) return;
    if (pausadoRef.current) return;
    // Chofer/Cliente: primer poll usa fetcher completo, los siguientes usan el ligero
    const fetchKey = (rol === 'chofer' && pollCountRef.current > 0) ? 'chofer_poll'
      : (rol === 'cliente' && pollCountRef.current > 0) ? 'cliente_poll'
      : rol;
    const fetcher = fetchersPorRol[fetchKey];
    if (!fetcher) return;
    pollCountRef.current += 1;
    try {
      const datos = await fetcher();
      fallosConsecutivosRef.current = 0;
      intervaloActualRef.current = rol === 'chofer' ? 20000 : 15000;

      // ── Detección de notificaciones nuevas (única fuente de verdad) ──
      if (Array.isArray(datos.notificaciones)) {
        const vistas = getVistas();
        if (!iniciado.current) {
          iniciado.current = true;
          addVistas(datos.notificaciones.map(n => n.id));
          // Primera carga: NO marcar como leídas — el badge persiste hasta que el usuario
          // abre la campana. Emitir notificaciones individuales (máx 5, más recientes primero)
          // para que cada toast tenga datos específicos y navegación directa.
          const noLeidas = datos.notificaciones.filter(n => !n.leida);
          if (noLeidas.length > 0) {
            const porEmitir = [...noLeidas]
              .sort((a, b) => new Date(b.creadoEn) - new Date(a.creadoEn))
              .slice(0, 5);
            window.dispatchEvent(new CustomEvent('choyero_notif_nueva', {
              detail: { notificaciones: porEmitir },
            }));
          }
        } else {
          const nuevas = datos.notificaciones.filter(n => !vistas.has(n.id) && !n.leida);
          addVistas(datos.notificaciones.map(n => n.id));
          if (nuevas.length > 0) {
            window.dispatchEvent(new CustomEvent('choyero_notif_nueva', { detail: { notificaciones: nuevas } }));
          }
        }
      }

      setStore(prev => ({ ...prev, ...datos }));
      updateStore(s => ({ ...s, ...datos }));
    } catch (e) {
      console.warn('[useApiStore] Error cargando datos:', e.message);
      fallosConsecutivosRef.current += 1;
      const nuevoIntervalo = Math.min(intervaloActualRef.current * 2, 60000);
      if (nuevoIntervalo !== intervaloActualRef.current) {
        intervaloActualRef.current = nuevoIntervalo;
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = setInterval(recargar, intervaloActualRef.current);
        }
      }
    }
  }, [rol]);

  const pausarPolling = useCallback(() => { pausadoRef.current = true; }, []);
  const reanudarPolling = useCallback(() => {
    pausadoRef.current = false;
    recargar(); // recargar inmediatamente al reanudar
  }, [recargar]);

  // Carga inicial — limpiar keys específicas del usuario anterior antes de cargar
  useEffect(() => {
    if (!rol) return;

    const keysUsuario = [
      'misReferidos', 'misReferidosN1', 'misReferidosN2', 'misReferidosN3',
      'redResumen', 'comisionesRed', 'notificaciones', 'solicitudes', 'viajes',
      'cotizaciones', 'retornosVacios', 'miEmpresa', 'usuarioPerfil',
    ];
    iniciado.current = false; // resetear para re-inicializar vistas al cambiar de sesión
    pollCountRef.current = 0;

    const bootstrap = consumeBootstrapPendiente();
    if (bootstrap) {
      // Datos del login disponibles — poblar store de inmediato, sin pantalla vacía
      setStore(prev => {
        const limpio = { ...prev };
        keysUsuario.forEach(k => { limpio[k] = Array.isArray(prev[k]) ? [] : null; });
        Object.entries(bootstrap).forEach(([k, v]) => { if (v !== null && v !== undefined) limpio[k] = v; });
        return limpio;
      });
      // Actualizar en background sin mostrar spinner
      recargar();
    } else {
      // Flujo normal (recarga de página, refresh de token, etc.)
      setStore(prev => {
        const limpio = { ...prev };
        keysUsuario.forEach(k => { limpio[k] = Array.isArray(prev[k]) ? [] : null; });
        return limpio;
      });
      setCargandoApi(true);
      recargar().finally(() => setCargandoApi(false));
    }

    const intervalo = rol === 'chofer' ? 20000 : 15000;
    intervalRef.current = setInterval(recargar, intervalo);

    // Pausar polling cuando la pantalla se apaga o la app va al fondo
    const onVisibility = () => {
      pausadoRef.current = document.hidden;
      // Al volver al frente, recargar inmediatamente
      if (!document.hidden) recargar();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      clearInterval(intervalRef.current);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [rol, recargar]);

  // Detener polling inmediatamente al hacer logout (rol se vuelve null)
  useEffect(() => {
    if (!rol && intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, [rol]);

  // Escuchar cambios locales del store (compatibilidad con funciones de store.js)
  useEffect(() => {
    const handleSync = () => setStore(prev => {
      const fromStore = getStore();
      // Las notificaciones SOLO vienen de la API (recargar), nunca del sync local.
      // Esto evita que un updateStore() vacíe notificaciones y rompa el dedup de toasts.
      return { ...fromStore, notificaciones: prev.notificaciones };
    });
    window.addEventListener('choyero_sync', handleSync);
    window.addEventListener('storage', handleSync);
    return () => {
      window.removeEventListener('choyero_sync', handleSync);
      window.removeEventListener('storage', handleSync);
    };
  }, []);

  // Recargar cuando llega un evento de Socket.io
  useEffect(() => {
    const handleRealtime = () => recargar();
    window.addEventListener('choyero_realtime', handleRealtime);
    return () => window.removeEventListener('choyero_realtime', handleRealtime);
  }, [recargar]);

  // dispatch compatible con el store original
  const dispatch = useCallback((updater) => {
    const updated = updateStore(updater);
    setStore(updated);
  }, []);

  return { store, dispatch, recargar, cargandoApi, pausarPolling, reanudarPolling };
};

export default useApiStore;
